/**
 * size-guide.js — Size Guide popup controller
 *
 * Wires [data-size-guide-trigger] (in variant-picker.liquid) to
 * [data-size-guide-modal] (snippets/size-guide-modal.liquid).
 *
 * Behavior:
 *   - Click on trigger → showModal()
 *   - Click on close button, ESC, or backdrop click → close()
 *   - Inch / Cm toggle buttons swap [aria-selected] + .is-active visual state.
 *     V1 does NOT convert numbers — the "in" and "cm" datasets are identical
 *     (placeholder values from Figma). Future: swap td values from data-* maps.
 *   - Body scroll lock via .sg-modal-open while open.
 *   - Focus management: close button receives focus on open; trigger receives
 *     focus on close.
 */

(function () {
  'use strict';

  const dialog = document.querySelector('[data-size-guide-modal]');
  if (!dialog) return;

  let lastTrigger = null;

  function openModal(trigger) {
    if (typeof dialog.showModal !== 'function') return; // graceful: ignore on legacy browsers
    if (dialog.open) return;
    lastTrigger = trigger || null;
    dialog.showModal();
    document.body.classList.add('sg-modal-open');

    // Move focus to close button
    const closeBtn = dialog.querySelector('[data-size-guide-close]');
    if (closeBtn) closeBtn.focus();
  }

  function closeModal() {
    if (!dialog.open) return;
    dialog.close();
  }

  // ── Open delegate ───────────────────────────────────────────
  document.addEventListener('click', function (e) {
    const trigger = e.target.closest('[data-size-guide-trigger]');
    if (trigger) {
      e.preventDefault();
      openModal(trigger);
    }
  });

  // ── Close delegate (close button + backdrop) ────────────────
  dialog.addEventListener('click', function (e) {
    // Close button
    if (e.target.closest('[data-size-guide-close]')) {
      closeModal();
      return;
    }
    // Backdrop click — when target IS the dialog itself (panel is the inner div)
    if (e.target === dialog) {
      closeModal();
    }
  });

  // ── On close (ESC or programmatic) ──────────────────────────
  dialog.addEventListener('close', function () {
    document.body.classList.remove('sg-modal-open');
    if (lastTrigger && typeof lastTrigger.focus === 'function') {
      lastTrigger.focus();
    }
    lastTrigger = null;
  });

  // ── Unit toggle (Inch / Cm) ─────────────────────────────────
  // Each <td> in the size table carries data-cm with the canonical centimeter
  // value. On unit switch, walk all cells and render either the cm value or
  // its inch equivalent (cm × 0.3937, rounded to 1 decimal place).
  const CM_TO_IN = 0.3937;
  const dataCells = dialog.querySelectorAll('[data-size-guide-table] td[data-cm]');

  function renderCells(unit) {
    dataCells.forEach(function (td) {
      const cm = parseFloat(td.dataset.cm);
      if (Number.isNaN(cm)) return;
      if (unit === 'in') {
        const inches = cm * CM_TO_IN;
        // Round to nearest half-inch for clean kidswear sizing
        const rounded = Math.round(inches * 2) / 2;
        td.textContent = rounded % 1 === 0 ? rounded.toFixed(0) : rounded.toFixed(1);
      } else {
        td.textContent = String(cm);
      }
    });
  }

  const unitBtns = dialog.querySelectorAll('[data-size-guide-unit]');
  unitBtns.forEach(function (btn) {
    btn.addEventListener('click', function () {
      const unit = btn.dataset.sizeGuideUnit;
      unitBtns.forEach(function (b) {
        const isActive = b === btn;
        b.classList.toggle('is-active', isActive);
        b.setAttribute('aria-selected', isActive ? 'true' : 'false');
      });
      dialog.dataset.activeUnit = unit;
      renderCells(unit);
    });
  });

  // Default to inches on first open (matches initial active state).
  renderCells('in');
})();
